import React from 'react';
import { Box, Checkbox } from '@mui/material';
import { IconGraphy, Badge, Typography } from '@leapeasy/ui-kit';

export const CHECKBOX_COLOR = '#69236b';
export const SELECTOR_CHECKBOX_SX = {
  color: CHECKBOX_COLOR,
  '&.Mui-checked': {
    color: CHECKBOX_COLOR,
  },
  '&.MuiCheckbox-indeterminate': {
    color: CHECKBOX_COLOR,
  },
};

const TABLE_TEXT_COLOR = '#3C393D';

const renderSelectorCheckbox = (checked, onChange, indeterminate = false) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
    <Checkbox
      disableRipple
      sx={SELECTOR_CHECKBOX_SX}
      checked={checked}
      indeterminate={indeterminate}
      onChange={onChange}
    />
  </Box>
);

const textCell = (value) => (
  <Typography variant="body2" style={{ color: TABLE_TEXT_COLOR }}>
    {value}
  </Typography>
);

const dispositionedColumn = {
  name: 'Dispositioned',
  key: 'dispositioned',
  options: {
    sort: true,
    customBodyRenderer: (value) => {
      const normalizedValue = String(value).toLowerCase() === 'true' ? 'true' : 'false';
      return (
        <Badge
          background="#F3F1F4"
          color={normalizedValue === 'true' ? 'tomato' : 'parisGreen'}
          label={normalizedValue}
          rounded
          textSize="medium"
        />
      );
    },
  },
};

const landlordNameColumn = {
  name: 'Operator',
  key: 'landlord_name',
  options: {
    sort: true,
    flex: '140px 1 1',
    customBodyRenderer: (value) => textCell(value),
  },
};

const primaryContactColumn = {
  name: 'Primary Contact',
  key: 'primary_contact',
  options: {
    sort: true,
    customBodyRenderer: (value) => textCell(value),
  },
};

export const getInviteUserColumns = ({
  selectedProperties,
  onToggleProperty,
  onClickInviteUserDetail,
  showSelectorColumn = true,
  showDelegationColumns = false,
  showPrimaryContact = false,
  showDetailColumn = true,
}) => {
  const columns = [
    ...(showSelectorColumn
      ? [
          {
            name: '',
            key: 'selector',
            options: {
              width: 48,
              minWidth: 48,
              shrink: false,
              excludeFromExport: true,
              customBodyRenderer: (value) => {
                const id = value;
                return renderSelectorCheckbox(selectedProperties.includes(id), () =>
                  onToggleProperty(id),
                );
              },
            },
          },
        ]
      : []),
    {
      name: 'Name',
      key: 'name',
      options: {
        sort: true,
        customBodyRenderer: (value) => textCell(value),
      },
    },
    {
      name: 'Property Type',
      key: 'building_type',
      options: {
        sort: true,
        customBodyRenderer: (value) => (
          <Badge
            background="#FFF0EA"
            color="tomato"
            label={value || 'Undefined'}
            rounded
            textSize="small"
          />
        ),
      },
    },
    {
      name: 'Phone Number',
      key: 'phone',
      options: {
        sort: true,
        customBodyRenderer: (value) => textCell(value),
      },
    },
    {
      name: 'Address',
      key: 'address',
      options: {
        sort: true,
        flex: '150px 1 1',
        customBodyRenderer: (value) => textCell(value),
      },
    },
    ...(showPrimaryContact ? [primaryContactColumn] : []),
    {
      name: 'Email Address',
      key: 'email_address',
      options: {
        sort: true,
        flex: '160px 1 1',
        customBodyRenderer: (value) => textCell(value),
      },
    },
    {
      name: 'Unit Count',
      key: 'total_of_units',
      options: {
        sort: true,
        customBodyRenderer: (value) => textCell(value),
      },
    },
    {
      name: 'Student Housing',
      key: 'student_housing',
      options: {
        sort: true,
        customBodyRenderer: (value) => {
          const normalizedValue = String(value).toLowerCase() === 'true' ? 'true' : 'false';
          return (
            <Badge
              background="#F3F1F4"
              color={normalizedValue === 'true' ? 'parisGreen' : 'tomato'}
              label={normalizedValue}
              rounded
              textSize="medium"
            />
          );
        },
      },
    },
    ...(showDelegationColumns ? [dispositionedColumn, landlordNameColumn] : []),
  ];

  if (showDetailColumn) {
    columns.push({
      name: 'Detail',
      key: 'detail',
      options: {
        customBodyRenderer: (_, row) => (
          <IconGraphy
            icon="FileFolder.StickyNote2"
            style={{ color: '#702572', cursor: 'pointer' }}
            onClick={() => onClickInviteUserDetail(row[0])}
          />
        ),
      },
    });
  }

  return columns;
};
